import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiResetPassword } from '../utils/backendApi';
import { Lock, AlertTriangle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import Logo from '../components/ui/Logo';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const inputStyle = {
    width: '100%', padding: '12px 14px 12px 42px', borderRadius: 10,
    border: '1px solid rgba(51,65,85,0.6)', background: 'rgba(15,23,42,0.6)',
    color: '#e2e8f0', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
    outline: 'none', boxSizing: 'border-box',
  };

  const eyeBtn = {
    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: '#475569', cursor: 'pointer',
    display: 'flex', alignItems: 'center', padding: 2,
  };

  const handleReset = async () => {
    setError('');
    if (!password) return setError('Please enter a new password');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return setError('Password needs uppercase, lowercase, and a number');
    }
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    try {
      await apiResetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0e17 0%, #111827 40%, #0f172a 100%)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
        backgroundImage: 'linear-gradient(rgba(99,102,241,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px' }} />
      <div style={{ position: 'absolute', top: '10%', left: '20%', width: 400, height: 400, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      <div className="auth-card animate-slide-up" style={{
        width: '100%', maxWidth: 440, background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(20px)',
        borderRadius: 20, border: '1px solid rgba(99,102,241,0.15)', padding: '48px 40px',
        position: 'relative', zIndex: 1, margin: '0 16px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(99,102,241,0.05)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Logo size={30} />
        </div>

        {/* No token */}
        {!token && (
          <div style={{ textAlign: 'center' }}>
            <AlertTriangle size={40} color="#ef4444" style={{ marginBottom: 12 }} />
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 8px 0' }}>Invalid Reset Link</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px 0' }}>This link is missing required information. Please request a new password reset.</p>
            <button onClick={() => navigate('/login')} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)', background: 'transparent', color: '#818cf8', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Go to Sign In
            </button>
          </div>
        )}

        {/* Success */}
        {token && done && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 16px', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={32} color="#10b981" />
            </div>
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 8px 0' }}>Password Reset!</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px 0', lineHeight: 1.5 }}>
              Your password has been updated. You can now sign in with your new password.
            </p>
            <button onClick={() => navigate('/login')} style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #6366f1, #7c3aed)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              Sign In
            </button>
          </div>
        )}

        {/* Reset form */}
        {token && !done && (
          <div>
            <h3 style={{ color: '#f1f5f9', fontSize: 18, margin: '0 0 6px 0' }}>Set New Password</h3>
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 20px 0', lineHeight: 1.5 }}>
              Choose a strong password for your account.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                <input
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="New Password"
                  type={showPassword ? 'text' : 'password'}
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onKeyDown={e => e.key === 'Enter' && handleReset()}
                />
                <button type="button" style={eyeBtn} onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{ position: 'absolute', left: 14, top: 14, color: '#475569' }} />
                <input
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password"
                  type={showConfirm ? 'text' : 'password'}
                  style={{
                    ...inputStyle, paddingRight: 40,
                    borderColor: confirmPassword && confirmPassword !== password ? 'rgba(239,68,68,0.5)' : 'rgba(51,65,85,0.6)',
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleReset()}
                />
                <button type="button" style={eyeBtn} onClick={() => setShowConfirm(v => !v)} tabIndex={-1}>
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
                Password must have: 8+ characters, uppercase, lowercase, number
              </div>
              {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} />{error}
                </div>
              )}
              <button
                onClick={handleReset}
                disabled={loading}
                style={{ width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', background: loading ? '#475569' : 'linear-gradient(135deg, #6366f1, #7c3aed)', color: '#fff', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(99,102,241,0.3)' }}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' }}>
                Back to Sign In
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 480px) {
          .auth-card { padding: 32px 20px !important; border-radius: 16px !important; margin: 0 12px !important; }
        }
      `}</style>
    </div>
  );
}
